import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, FileText, ExternalLink, Download, RefreshCw } from 'lucide-react';

export function AdminDocumentationTab() {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-brand-navy/10 flex items-center justify-center flex-shrink-0">
            <BookOpen className="h-6 w-6 text-brand-navy" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold font-heading text-slate-800">Internal documentation</h2>
            <p className="text-sm text-slate-600 mt-1">
              The complete operational reference for SLL&nbsp;BriteHub &mdash; architecture, every API endpoint,
              data models, the admin guide, integrations, deployment, testing and troubleshooting playbooks &mdash;
              plus a polished, branded <strong>Features &amp; Functionality</strong> PDF you can share externally.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => navigate('/docs')} className="bg-brand-navy hover:bg-brand-navy/90 text-white" data-testid="open-documentation-btn">
                <ExternalLink className="h-4 w-4 mr-2" /> Open documentation portal
              </Button>
              <Button onClick={() => navigate('/docs?doc=08-admin-guide')} variant="outline" data-testid="open-admin-guide-btn">
                <FileText className="h-4 w-4 mr-2" /> Admin guide
              </Button>
              <Button onClick={() => navigate('/docs?doc=05-api-reference')} variant="outline" data-testid="open-api-reference-btn">
                <FileText className="h-4 w-4 mr-2" /> API reference
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-700 mb-1.5">
            <Download className="h-4 w-4 text-brand-sage" />
            <h3 className="font-semibold">Branded Features PDF</h3>
          </div>
          <p className="text-sm text-slate-600">
            A 21-page, business-ready document outlining every feature of the platform. Includes the
            BriteHub logo, brand palette and a polished cover. Perfect for partners, prospective sponsors
            and onboarding new team members.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => navigate('/docs?doc=04-features')}
            data-testid="open-features-doc-btn"
          >
            View source &rarr;
          </Button>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-700 mb-1.5">
            <RefreshCw className="h-4 w-4 text-brand-sage" />
            <h3 className="font-semibold">Always up to date</h3>
          </div>
          <p className="text-sm text-slate-600">
            When a new feature ships, regenerate the PDF in one click from the documentation portal.
            All cached previews refresh automatically so the next person to open the document sees
            the latest content.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => navigate('/docs')}
            data-testid="open-portal-refresh-btn"
          >
            Open portal &rarr;
          </Button>
        </Card>
      </div>
    </div>
  );
}
